import { Button, Modal, Table, TableProps, message } from 'antd';
import type { PhysicalProduct } from './type';
import { EditProduct } from './EditProduct';
import useModal from 'hooks/useModal';
import { useRequest } from 'ahooks';
import { deleteProduct } from './service';
import SpuMore from './SpuMore';
import { countSku } from '@knowledge/common/helpers/sku';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { gt, isEmpty } from 'lodash-es';
import { safeParse } from './util';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';

interface ProductTableProps<T> extends TableProps<T> {
  onRefresh: () => void;
}

export const ProductTable = (props: ProductTableProps<PhysicalProduct>) => {
  const { onRefresh, dataSource, ...restProps } = props;
  const { t } = useTranslate();
  const { meta, show, visible, hide } = useModal<PhysicalProduct>();
  const { run, loading } = useRequest((id) => deleteProduct(id), {
    manual: true,
    onSuccess: () => {
      message.success(t('phrase.删除成功'));
      onRefresh?.();
    },
  });

  return (
    <>
      {visible && (
        <EditProduct
          visible={visible}
          onClose={() => {
            hide();
            onRefresh?.();
          }}
          productData={meta}
          listData={props.dataSource}
        />
      )}
      <Table
        columns={[
          {
            dataIndex: 'name',
            key: 'name',
            title: t('bs_9'),
            render: (_, record) => {
              return <ProductNameRender product={record} />;
            },
          },
          {
            key: 'spus',
            dataIndex: 'spus',
            title: t('todo.关联商品'),
            width: 200,
            render: (value) => {
              if (value.length === 0) return <span>{t('single_x_spu', { x: 0 })}</span>;
              return (
                <SpuMore spus={value}>
                  <div>
                    <span>{t('single_x_spu', { x: value.length })}</span>&nbsp;
                    <span>{t('single_x_sku', { x: countSku(value, 'skus') })}</span>
                  </div>
                </SpuMore>
              );
            },
          },
          {
            title: t('操作'),
            key: 'operate',
            width: 120,
            render: (_, record) => {
              return (
                <div>
                  <Button onClick={() => show(record)} type="text" className="!px-[4px]">
                    {t('marketing.编辑')}
                  </Button>
                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: t('bs_76'),
                        okButtonProps: {
                          loading,
                        },
                        onOk: () => {
                          run(record.id);
                        },
                      });
                    }}
                    type="text"
                    className="!px-[4px]"
                  >
                    {t('marketing.删除')}
                  </Button>
                </div>
              );
            },
          },
        ]}
        dataSource={dataSource}
        pagination={
          gt(dataSource?.length, 10)
            ? {
                simple: true,
              }
            : false
        }
        {...restProps}
      />
    </>
  );
};

export const ProductNameRender = (props: { product?: PhysicalProduct }) => {
  const name = useProductNameRender(props);
  return <>{name}</>;
};

export const useProductNameRender = (props: { product?: PhysicalProduct }) => {
  const { product } = props;
  const lang = useSystemLanguageValue();

  return useMemo(() => {
    if (!product) return '';

    return getProductName(product, lang);
  }, [product, lang]);
};

export const getProductName = (product: PhysicalProduct, lang: General.Language) => {
  if (lang === 'zh-CN') {
    return product.name;
  } else {
    const newName = safeParse(product.multiLang)['en-US'];
    return isEmpty(newName) ? product.name : newName;
  }
};
